import { cn } from "@/lib/utils";
import { ButtonHTMLAttributes } from "react";

import { IconBox } from "../IconBox";
import { Text } from "../Text";

type ToggleButton = {
    name?: IconName;
    rightIcon?: IconName | null;
    active?: boolean;
    interaction?: "hover";
    onRightIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function ToggleButton({
    children,
    active = false,
    interaction,
    name,
    rightIcon,
    onRightIconClick,
    className,
    ...props
}: ToggleButton) {
    const isHoverInteraction = interaction === "hover";

    return (
        <button
            type="button"
            data-interaction={interaction}
            className={cn(
                "group flex w-full items-center gap-2 rounded-medium px-4 py-2",
                active ? "bg-secondary-hover" : "bg-transparent",
                className
            )}
            {...props}
        >
            {name && (
                <IconBox
                    size="medium"
                    name={name}
                    color={
                        active
                            ? "text-secondary-main"
                            : cn(
                                  "text-grey-400",
                                  !isHoverInteraction &&
                                      "group-hover:text-secondary-main",
                                  isHoverInteraction && "text-secondary-main"
                              )
                    }
                />
            )}
            {children && (
                <Text
                    variant="body2"
                    color={active ? "text.primary" : "text.disabled"}
                    className={cn(
                        `min-w-0 flex-grow overflow-hidden whitespace-nowrap
                        text-left`,
                        !active &&
                            !isHoverInteraction &&
                            "group-hover:text-text-primary",
                        !active && isHoverInteraction && "text-text-primary"
                    )}
                >
                    {children}
                </Text>
            )}
            {rightIcon && (
                <div
                    className="group/right-icon flex cursor-pointer items-center
                        justify-center rounded-medium bg-transparent p-0.5
                        transition-colors hover:bg-grey-a100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRightIconClick?.(e);
                    }}
                >
                    <IconBox
                        size="medium"
                        name={rightIcon}
                        color="text-grey-400 group-hover/right-icon:text-text-secondary"
                    />
                </div>
            )}
        </button>
    );
}
